import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp, MapPin } from "lucide-react";

const MAX_VISIBLE = 8;

export type LegendItem = { label: string; color: string; count: number };

interface MapLegendProps {
	children: ReactNode;
}

export const MapLegend = ({ children }: MapLegendProps) => {
	const [open, setOpen] = useState(false);

	return (
		<div className="absolute right-3 bottom-8 z-10 flex flex-col items-end">
			<div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-md overflow-hidden">
				{open && <div className="px-3 pt-3 pb-1.5 w-[200px]">{children}</div>}
				<button
					onClick={() => setOpen(prev => !prev)}
					className="flex items-center gap-1.5 px-3 py-1.5 w-full text-left hover:bg-gray-50 transition-colors"
				>
					<MapPin className="h-3 w-3 text-gray-500" />
					<span className="text-[11px] font-medium text-gray-600">Legend</span>
					{open ? (
						<ChevronDown className="h-3 w-3 text-gray-400 ml-auto" />
					) : (
						<ChevronUp className="h-3 w-3 text-gray-400 ml-auto" />
					)}
				</button>
			</div>
		</div>
	);
};

interface LegendItemListProps {
	items: LegendItem[];
	maxVisible?: number;
}

export const LegendItemList = ({ items, maxVisible = MAX_VISIBLE }: LegendItemListProps) => {
	const [showAll, setShowAll] = useState(false);
	const visible = !showAll ? items.slice(0, maxVisible) : items;
	const hasMore = items.length > maxVisible;

	return (
		<div className="space-y-1">
			{visible.map(item => (
				<div key={item.label} className="flex items-center gap-2">
					<span
						className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
						style={{ backgroundColor: item.color }}
					/>
					<span className="text-xs text-gray-700 truncate flex-1">{item.label}</span>
					<span className="text-[10px] text-gray-400 tabular-nums">{item.count}</span>
				</div>
			))}
			{hasMore && (
				<button onClick={() => setShowAll(prev => !prev)} className="text-[11px] text-blue-600 hover:underline">
					{showAll ? "Show less" : `+${items.length - maxVisible} more`}
				</button>
			)}
		</div>
	);
};
